import { api } from '@/core/api/client'
import type { Invoice, InvoiceItem, InvoiceStatus, InvoiceType } from '@/features/classes/stores/types'

const invoicesCollection = api.collection<Invoice>('invoices')
const invoiceItemsCollection = api.collection<InvoiceItem>('invoice-items')

export async function fetchInvoices(): Promise<Invoice[]> {
  return invoicesCollection.getFullList({ sort: '-issuedAt' })
}

export async function fetchInvoiceById(id: string): Promise<Invoice | null> {
  try {
    return await invoicesCollection.getOne(id)
  } catch {
    return null
  }
}

export async function fetchInvoiceByNumber(invoiceNumber: string): Promise<Invoice | null> {
  try {
    const all = await invoicesCollection.getFullList()
    return all.find((i) => i.invoiceNumber === invoiceNumber) || null
  } catch {
    return null
  }
}

export async function fetchInvoicesByCompany(companyId: string): Promise<Invoice[]> {
  const all = await invoicesCollection.getFullList()
  return all.filter((i) => i.companyId === companyId)
}

export async function fetchInvoicesByStatus(status: InvoiceStatus): Promise<Invoice[]> {
  const all = await invoicesCollection.getFullList()
  return all.filter((i) => i.status === status)
}

export async function fetchInvoiceItems(invoiceId: string): Promise<InvoiceItem[]> {
  const all = await invoiceItemsCollection.getFullList()
  return all.filter((i) => i.invoiceId === invoiceId)
}

export async function createInvoice(data: {
  invoiceNumber: string
  orderId?: string
  companyId: string
  type: InvoiceType
  issuedAt: string
  dueDate?: string
  status: InvoiceStatus
  subtotal: number
  taxRate: number
  taxAmount: number
  total: number
  notes?: string
}): Promise<Invoice> {
  return invoicesCollection.create(data)
}

export async function updateInvoice(
  id: string,
  data: Partial<{
    status: InvoiceStatus
    dueDate: string
    notes: string
    sentAt: string
    paidAt: string
  }>
): Promise<Invoice> {
  return invoicesCollection.update(id, data)
}

export async function createInvoiceItem(data: {
  invoiceId: string
  description: string
  quantity: number
  unitPrice: number
  total: number
}): Promise<InvoiceItem> {
  return invoiceItemsCollection.create(data)
}

export async function generateInvoiceNumber(): Promise<string> {
  const year = new Date().getFullYear()
  const invoices = await invoicesCollection.getFullList({ sort: '-invoiceNumber' })

  let sequence = 1
  const yearInvoices = invoices.filter((i) => i.invoiceNumber?.startsWith(`INV-${year}`))
  if (yearInvoices.length > 0) {
    const lastNumber = yearInvoices[0].invoiceNumber
    const match = lastNumber.match(/INV-(\d{4})-(\d{5})/)
    if (match) {
      sequence = parseInt(match[2], 10) + 1
    }
  }

  return `INV-${year}-${sequence.toString().padStart(5, '0')}`
}

export async function deleteInvoice(id: string): Promise<void> {
  await invoicesCollection.delete(id)
}

export async function sendInvoice(id: string): Promise<Invoice> {
  return invoicesCollection.update(id, {
    status: 'sent',
    sentAt: new Date().toISOString(),
  })
}
